import { FC, useEffect, useCallback, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useQuery } from 'hooks/useQuery';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useTranslations } from 'hooks/useTranslations';
import { useGetAccountStatusByEmailMutation } from 'store/customerService';
import { localizationSelector } from 'store/localizationState';
import {
  getConsentType,
  signUpSelector,
  submitCredentials
} from 'store/signupState';
import { authSelector } from 'store/authState';
import { CustomerAccountStatus } from 'models/customer';
import { AccountType, CreateAccountAttributes } from 'models/signup';
import { showErrorToast } from 'utils/toastUtils';
import {
  privacyPolicyUrlHusqvarna,
  privacyPolicyUrlConstruction
} from 'utils/constants';
import { getConsentTranslation } from 'utils/customerUtils';
import * as routes from 'router/routes';
import { isConstruction } from 'config';

import SeoHelmet from 'components/fragments/SeoHelmet';
import PageImageWrapper from 'components/UI/Page/PageImageWrapper';
import PageHeader from 'components/UI/Page/PageHeader';
import Heading, { Type } from 'components/UI/Heading';
import Button from 'components/UI/Buttons/Button';
import InputField from 'components/UI/InputField';
import Checkbox from 'components/UI/Checkbox';
import Loader from 'components/UI/Loader';

import passwordIcon from 'assets/vectors/password.svg';

import { Link } from 'styles';
import { Form, ButtonContainer } from '../styled';
import {
  FormText,
  MessageLink,
  SelectButtonGrid,
  CheckBoxContainer,
  CheckBoxText,
  AcceptConsent
} from './styled';

export const CreateAccountPage: FC = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    session,
    hasLoggedIn,
    isLoading: isAuthLoading
  } = useAppSelector(authSelector);
  const { languageCode } = useAppSelector(localizationSelector);
  const { data, isLoading, error } = useAppSelector(signUpSelector);
  const [getAccountStatus, accountStatusResult] =
    useGetAccountStatusByEmailMutation();

  // Params
  const [defaultEmail, accountType] = useQuery('email', 'accountType');

  // Define Zod schema
  const schema = useMemo(() => {
    const baseSchema = z.object({
      email: z
        .email({ message: t('signup.form.email_invalid') })
        .min(1, { message: t('signup.form.email_required') }),
      emailMarketing: z.boolean()
    });

    // If user is authenticated, password fields are optional
    if (session.authenticated) {
      return baseSchema.extend({
        password: z.string().optional(),
        confirmedPassword: z.string().optional()
      });
    }

    // If user is not authenticated, password fields are required
    return baseSchema
      .extend({
        password: z
          .string()
          .min(1, { message: t('signup.form.password_required') })
          .min(6, { message: 'input.password_too_short' }),
        confirmedPassword: z.string().min(1, {
          message: t('signup.form.password_required')
        })
      })
      .refine((data) => data.password === data.confirmedPassword, {
        message: t('signup.form.unconfirmed_password'),
        path: ['confirmedPassword']
      });
  }, [t, session.authenticated]);

  const {
    handleSubmit,
    register,
    formState: { errors },
    setError,
    resetField,
    control
  } = useForm<CreateAccountAttributes>({
    defaultValues: {
      email: defaultEmail ?? session.user?.attributes.username ?? data.email,
      password: data.password,
      confirmedPassword: data.confirmedPassword,
      emailMarketing: false
    },
    resolver: zodResolver(schema)
  });

  // Watch - using useWatch hook for React 19 compiler compatibility
  const acceptEmailMarketing = useWatch({
    control,
    name: 'emailMarketing'
  });

  // Get consent type
  useEffect(() => {
    dispatch(getConsentType());
  }, [dispatch]);

  // Error handling
  useEffect(() => {
    if (error) {
      showErrorToast(error.message);
    }
  }, [error]);

  // Check account status
  const onCheckAccountStatus = useCallback(
    async (email: string): Promise<CustomerAccountStatus | null> => {
      try {
        const { data } = await getAccountStatus(email);

        if (!data) {
          setError('email', {
            message: 'errors.unknown_error',
            type: 'validate'
          });
          return null;
        }

        // User account already exist
        if (data.loginAccountExists && !session.authenticated) {
          setError('email', {
            message: 'errors.email_unavailable',
            type: 'validate'
          });
          return null;
        }

        return data;
      } catch (error: any) {
        // Handle email domain blocked error
        if (error.code === 'email.domain.blocked') {
          setError('email', { message: error.message });
        }

        // Show error toast
        showErrorToast(error.message);
        return null;
      }
    },
    [getAccountStatus, session, setError]
  );

  // Submit form
  const submitForm: SubmitHandler<CreateAccountAttributes> = useCallback(
    async (values) => {
      const accountStatus = await onCheckAccountStatus(values.email);
      if (!accountStatus) {
        return;
      }

      // Submit email and password
      dispatch(
        submitCredentials({
          ...values,
          accountStatus,
          accountType: accountType as AccountType
        })
      );

      // Navigate to next step
      navigate(
        accountType === 'business' || isConstruction
          ? routes.SIGNUP_COMPANY
          : routes.SIGNUP_PROFILE
      );
    },
    [dispatch, accountType, navigate, onCheckAccountStatus]
  );

  // Actions
  const onLogin = useCallback(() => navigate(routes.LOGIN), [navigate]);
  const onTabClick = useCallback(
    (type: string) => () => navigate(`${routes.SIGNUP}?accountType=${type}`),
    [navigate]
  );

  // Check if organization
  const isBusiness = useMemo(() => accountType === 'business', [accountType]);

  // Account type tabs
  const accountTypeTabs = useMemo(() => {
    if (isConstruction) {
      return null;
    }
    return (
      <div>
        <Heading type={Type.h4}>{t('signup.form.label_customer_type')}</Heading>
        <SelectButtonGrid>
          <Button
            color={isBusiness ? 'background' : 'primary'}
            textColor={isBusiness ? 'primary' : 'onPrimary'}
            onClick={onTabClick('private')}
            border={isBusiness}
            fullWidth
          >
            {t('signup.tab_private')}
          </Button>
          <Button
            color={isBusiness ? 'primary' : 'background'}
            textColor={isBusiness ? 'onPrimary' : 'primary'}
            onClick={onTabClick('business')}
            border={!isBusiness}
            fullWidth
          >
            {t('signup.tab_business')}
          </Button>
        </SelectButtonGrid>
      </div>
    );
  }, [t, onTabClick, isBusiness]);

  // Consent texts
  const consentTexts = useMemo(
    () => getConsentTranslation(data.consentType?.texts, languageCode),
    [languageCode, data.consentType]
  );

  // Show Login Link
  const showEmailHelperLink = useMemo(() => {
    if (errors.email?.message === 'errors.email_unavailable') {
      return (
        <MessageLink to={routes.LOGIN}>{t('login.meta_title')}</MessageLink>
      );
    }
    return null;
  }, [errors.email, t]);

  // Redirect to login if auth is required but user is not authenticated;
  if (hasLoggedIn && !session.authenticated) {
    return <Navigate to={routes.LOGIN} />;
  }

  // Loading
  if (isLoading || accountStatusResult.isLoading || isAuthLoading) {
    return <Loader center padding size="large" />;
  }

  // Redirect to root if user is authenticated and has customer account
  if (session.authenticated && session.user?.attributes.customerId) {
    return <Navigate to={routes.ROOT} />;
  }

  const privacyPolicyUrl = isConstruction
    ? privacyPolicyUrlConstruction
    : privacyPolicyUrlHusqvarna;

  return (
    <PageImageWrapper alignTop>
      <SeoHelmet title="signup.create_account.meta_title" />
      <PageHeader
        title={t('signup.create_account.title')}
        subtitle={t('signup.create_account.subtitle')}
        image={passwordIcon}
        imageAlt="Create account"
      />
      {accountTypeTabs}
      <Form onSubmit={handleSubmit(submitForm)}>
        {!hasLoggedIn && (
          <>
            <InputField
              name="email"
              description="signup.form.label_email"
              error={errors.email}
              helperAction={showEmailHelperLink}
              disabled={defaultEmail !== null}
              register={register('email')}
              reset={resetField}
            />
            <InputField
              name="password"
              description="signup.form.label_password"
              type="password"
              error={errors.password}
              register={register('password')}
              reset={resetField}
            />
            <InputField
              name="confirmedPassword"
              description="signup.form.label_password_confirmation"
              type="password"
              error={errors.confirmedPassword}
              register={register('confirmedPassword')}
              reset={resetField}
            />
          </>
        )}
        <AcceptConsent>
          <CheckBoxContainer>
            <Checkbox
              id="emailMarketing"
              register={register('emailMarketing')}
              isChecked={acceptEmailMarketing}
            />
          </CheckBoxContainer>
          <div>
            <Heading type={Type.h3}>{consentTexts.title}</Heading>
            <CheckBoxText>{consentTexts.description}</CheckBoxText>
          </div>
        </AcceptConsent>
        <FormText>{t('signup.legal')}</FormText>
        <FormText>
          {t('signup.privacy_info_1')}
          <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
            {t('signup.privacy_policy')}
          </a>
          {t('signup.privacy_info_2')}
        </FormText>
        <FormText>
          {t('signup.contract_info_1')}
          <Link
            href={routes.TERMS_OF_USE}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('signup.terms_of_use')}
          </Link>
          {t('signup.contract_info_2')}
          <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
            {t('signup.privacy_policy_2', {
              defaultValue: t('signup.privacy_policy')
            })}
          </a>
          {t('signup.contract_info_3')}
        </FormText>
        <ButtonContainer>
          <Button submit fullWidth>
            {t('button.continue')}
          </Button>
          {!hasLoggedIn && (
            <Button
              onClick={onLogin}
              color="transparent"
              textColor={isConstruction ? 'grey4' : 'primary'}
            >
              {t('signup.create_account.btn_login')}
            </Button>
          )}
        </ButtonContainer>
      </Form>
    </PageImageWrapper>
  );
};
